import React, { useRef, useEffect, useCallback, Suspense } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useFormikContext, getIn } from 'formik';
import {
  FarmScheduleCoverageTypesByState,
  FarmScheduleCoverageType,
  lookupsJson,
  FarmRiskType
} from '@ourbranch/lookups';

import editIcon from 'core/assets/svg/edit.svg';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import { useToast } from 'core/components/toast';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { Tooltip } from 'core/components/tooltip';
import { LabelTooltip } from 'core/components/label-tooltip';
import {
  SwimmingPoolForm,
  InlandMarineForm,
  RelatedPrivateStructuresForm,
  WellPumpForm,
  DwellingUnderConstructionForm,
  EarthquakeCoverageForm
} from '../sub-risks';
import { BarndomininumDwellingForm } from 'common/components/farm/barndominium-dwelling-form';
import { MobileHomeForm } from 'common/components/farm/mobile-home-form';
import { RenovationFieldsForm } from 'common/components/farm/renovation-fields-form';
import {
  CosmeticDamageExclusionSection,
  RoofExclusionSection,
  ExteriorWallSurfacingExclusionSection,
  OtherStructuresExclusionSection,
  StormDamageExclusionSection
} from '../exclusions';
import { Banner } from 'common/components/banner';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import IsolatedCoverageD from 'common/components/farm/isolated-coverage-d';
import { useStore } from 'core/store';
import { formatCoverageB } from '../../helpers';
import { getResidenceUsageOptions } from 'common/helpers/farm-helpers';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { brandConfig } from 'brand-config';
import { getCoverageAHelpText } from './coverage-a-help-text';

import useStyles from '../risk.styles';

const numFamiliesInDwellingOptions = [
  { id: '1', value: '1' },
  { id: '2', value: '2' },
  { id: '3', value: '3' },
  { id: '4', value: '4' }
];

const basementTypeOptions = lookupsJson.basementType;
const constructionTypeOptions = lookupsJson.farm.dwellingConstructionType;
const exteriorWallTypeOptions = lookupsJson.exteriorWallType;
const fireProtectiveDevicesOptions = lookupsJson.farm.fireProtectiveDevice;
const garageTypeOptions = lookupsJson.garageType;
const homeQualityOptions = lookupsJson.homeQuality;
const lossSettlementOptions = lookupsJson.farm.lossSettlement;
const roofLossSettlementOptions = lookupsJson.farm.roofLossSettlement;
const residenceUsageOptions = getResidenceUsageOptions(FarmRiskType.Fo1);
const roofShapeOptions = lookupsJson.roofShape;
const roofTypeOptions = lookupsJson.farm.dwellingRoofType;
const theftPreventionDevicesOptions = lookupsJson.farm.theftProtectiveDevice;
const windHailDeductibleOptions = lookupsJson.farm.windstormOrHail;
const heatingOptions = lookupsJson.farm.primaryHeatingSourceOptions;
const propertyTypeOptions = lookupsJson.farm.propertyType;

const FO1 = ({
  item,
  index,
  fieldName,
  removeRisk,
  setRiskFieldnameToEdit,
  canRemoveRisk,
  isLastRisk,
  riskIdString,
  locationAddress
}) => {
  const classes = useStyles();
  const {
    offer: { offer }
  } = useStore();
  const { inPolicyRoute } = useRouteHelper();
  const { setFieldValue, errors, initialValues, values } = useFormikContext();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const debounceTimerRef = useRef(null);
  const userEditingCoverageDRef = useRef(false);
  const toast = useToast();
  const rejections = offer?.rejections || [];
  const address = locationAddress;
  const coverageA = item.coverages?.coverageA;
  const hasBeenRated = !!item.id;

  const isBarndominium = item?.building?.propertyType === 'B';
  const isMobileHome = item?.building?.propertyType === 'MH';

  const needMoreData = rejections.some((rejection) => rejection.code === '50006') || ['FO4', 'FO6'].includes(item.type);
  const state = address?.state;
  const riskType = FarmRiskType.Fo1;
  const availableCoverageTypes = FarmScheduleCoverageTypesByState[state] || [];
  // Get Mine Subsidence coverage limit from brandConfig (GitHub issue #28634)
  const mineSubsidenceCoverageLimit = brandConfig.farm?.[state]?.mineSubsidenceCoverageLimit || 750000;
  // Get mobile home minimum $/sq ft from brandConfig, default $7 (GitHub issue #28969)
  const mobileHomeMinPerSqFt =
    brandConfig.farm?.[state]?.[riskType]?.businessRules?.coverageA?.mobileHome?.minPerSqFt || 7;

  const riskCoverages = getIn(values, fieldName)?.coverages || {};

  const initialCoverageA = getIn(initialValues, `${fieldName}.coverages.coverageA`);
  const initialBuilding = getIn(initialValues, `${fieldName}.building`);
  const initialConnectedHome = getIn(initialValues, `${fieldName}.connectedHome`);
  const initialCanEditCoverageAmount = getIn(initialValues, `${fieldName}.coverages.canEditCoverageAmount`);
  const coverageHasBeenUpdatedRef = useRef(false);

  // Update ref when coverage changes
  useEffect(() => {
    if (hasBeenRated) {
      coverageHasBeenUpdatedRef.current =
        initialCoverageA !== item.coverages?.coverageA &&
        initialCanEditCoverageAmount === item.coverages?.canEditCoverageAmount;
    }
  }, [initialCoverageA, item.coverages?.coverageA]);

  const toastRCEWarning = () => {
    if (coverageHasBeenUpdatedRef.current) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const isSelectedHeatingSourceUnitCountBased =
    heatingOptions.find((option) => option.id === item?.building?.primaryHeatingSource)?.unitCountBased ?? false;

  const isSelectedSecondaryHeatingSourceUnitCountBased =
    heatingOptions.find((option) => option.id === item?.building?.secondaryHeatingSource)?.unitCountBased ?? false;

  const propertyTypeLabel = propertyTypeOptions.find((option) => option.id === item?.building?.propertyType)?.value;
  const riskTitle = `FO-1 Basic${item?.building?.propertyType ? `: ${propertyTypeLabel}` : ''}`;

  // Check if residence usage is Vacant to disable Coverage D
  const residenceUsage = item?.building?.residenceUsage;
  const isCoverageDDisabled = residenceUsage === 'V';

  // Force Coverage D to $0 when residence usage is Vacant
  useEffect(() => {
    if (isCoverageDDisabled && item.coverages?.coverageD !== 0) {
      setFieldValue(`${fieldName}.coverages.coverageD`, 0);
    }
  }, [isCoverageDDisabled, item.coverages?.coverageD, fieldName, setFieldValue]);

  // Stable event handlers for Coverage D focus tracking
  const handleCoverageDFocus = useCallback(() => {
    userEditingCoverageDRef.current = true;
  }, []);

  const handleCoverageDBlur = useCallback(() => {
    // Add a small delay before clearing the flag to handle rapid focus changes
    setTimeout(() => {
      userEditingCoverageDRef.current = false;
    }, 100);
  }, []);

  // Set default values for ownership and home type for new risks only
  useEffect(() => {
    if (!isBarndominium && !isMobileHome) {
      // Only set defaults if values don't already exist (new risk)
      if (!item?.building?.ownershipStatus) {
        setFieldValue(`${fieldName}.building.ownershipStatus`, 'P');
      }
      if (!item?.building?.typeOfHome) {
        setFieldValue(`${fieldName}.building.typeOfHome`, '1');
      }
    }
  }, [
    fieldName,
    isBarndominium,
    isMobileHome,
    item?.building?.ownershipStatus,
    item?.building?.typeOfHome,
    setFieldValue
  ]);

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">{riskTitle}</Label>
            <div className={classes.divider} />
            <Label type="infoSubtitleSmall">{item.description}</Label>
            <IconButton onClick={() => setRiskFieldnameToEdit(fieldName)} className={classes.editButton}>
              <img className={classes.editIcon} src={editIcon} alt="Edit row" />
            </IconButton>
          </div>
        </div>
      }
      isRemovable={canRemoveRisk}
      onDelete={() => removeRisk(index)}
      disableRemoveButton={disableRiskForm}
      initialOpen={!disableRiskForm || isLastRisk}
      keepContentMounted
      id={riskIdString}
    >
      <Suspense
        fallback={
          <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
            <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
              <CircularProgress className={classes.loader} />
            </Grid>
          </Grid>
        }
      >
        {!hasBeenRated && !isBarndominium && !isMobileHome && (
          <Banner
            variant="notification"
            title={
              inPolicyRoute
                ? "We'll pull data for this dwelling or structure—go ahead and preview changes."
                : "We'll pull data for this dwelling or structure—go ahead and update the offer."
            }
            subtitle="Or enter the details manually if you prefer."
          />
        )}
        {((hasBeenRated && needMoreData) || (!hasBeenRated && (isBarndominium || isMobileHome))) && (
          <Banner
            variant="warning"
            title="Data for this dwelling or structure isn't available—please enter the details manually."
            subtitle={
              inPolicyRoute
                ? "This information won't auto-fill, even if you preview changes."
                : "This information won't auto-fill, even if you update the offer."
            }
          />
        )}
        <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
          {!isBarndominium && !isMobileHome && (
            <>
              <Grid item xs={12}>
                <Label type="infoTitleSmall">Residents</Label>
              </Grid>
              <Field
                xs={6}
                type="select"
                name={`${fieldName}.building.residenceUsage`}
                id={`${fieldName}.building.residenceUsage`}
                mode="light"
                label="Residence Type/Usage"
                options={residenceUsageOptions}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.residenceUsage) {
                    toastRCEWarning();
                  }
                }}
              />
              <Grid item xs={12}>
                <Label type="infoTitleSmall">Property</Label>
              </Grid>
              <Field
                xs={4}
                name={`${fieldName}.building.yearBuilt`}
                id={`${fieldName}.building.yearBuilt`}
                type="numeric"
                label="Year Built In"
                mode="light"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.yearBuilt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.sqFt`}
                name={`${fieldName}.building.sqFt`}
                mode="light"
                label="Above-ground sqft"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.sqFt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.basementSqFt`}
                name={`${fieldName}.building.basementSqFt`}
                mode="light"
                label="Basement sqft"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.basementSqFt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                options={numFamiliesInDwellingOptions}
                id={`${fieldName}.building.numFamiliesInDwelling`}
                name={`${fieldName}.building.numFamiliesInDwelling`}
                mode="light"
                label="Families in Dwelling"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFamiliesInDwelling) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                options={homeQualityOptions}
                id={`${fieldName}.building.homeQuality`}
                name={`${fieldName}.building.homeQuality`}
                mode="light"
                label="Home Quality"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.homeQuality) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numStories`}
                name={`${fieldName}.building.numStories`}
                mode="light"
                label="Stories"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numStories) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numHalfBathrooms`}
                name={`${fieldName}.building.numHalfBathrooms`}
                mode="light"
                label="Half Bathrooms"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numHalfBathrooms) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numFullBathrooms`}
                name={`${fieldName}.building.numFullBathrooms`}
                mode="light"
                label="Full Bathrooms"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFullBathrooms) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="select"
                options={constructionTypeOptions}
                id={`${fieldName}.building.constructionType`}
                name={`${fieldName}.building.constructionType`}
                mode="light"
                label="Construction Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.constructionType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.numFireplaces`}
                name={`${fieldName}.building.numFireplaces`}
                mode="light"
                label="Fireplaces"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFireplaces) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.numWoodBurningStoves`}
                name={`${fieldName}.building.numWoodBurningStoves`}
                mode="light"
                label="Wood Stoves"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numWoodBurningStoves) {
                    toastRCEWarning();
                  }
                }}
              />
              <Grid item xs={4}>
                <LabelTooltip
                  label="Solid Fuel-Burning Appliances"
                  tooltip={{
                    label: 'More Info',
                    onHoverText:
                      'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
                  }}
                  mode="light"
                >
                  <Field
                    type="numeric"
                    id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                    name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                    mode="light"
                    format={{
                      allowNegative: false
                    }}
                    disabled={disableRiskForm}
                    onChange={(val) => {
                      if (val !== initialBuilding?.numSolidFuelBurningAppliances) {
                        toastRCEWarning();
                      }
                    }}
                  />
                </LabelTooltip>
              </Grid>
              <Field
                xs={4}
                type="select"
                options={basementTypeOptions}
                id={`${fieldName}.building.basementType`}
                name={`${fieldName}.building.basementType`}
                mode="light"
                label="Basement/Foundation Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.basementType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="select"
                options={exteriorWallTypeOptions}
                id={`${fieldName}.building.exteriorWallType`}
                name={`${fieldName}.building.exteriorWallType`}
                mode="light"
                label="Exterior Wall Finish"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.exteriorWallType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.garageCarCapacity`}
                name={`${fieldName}.building.garageCarCapacity`}
                mode="light"
                label="Garage's Car Space"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.garageCarCapacity) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="select"
                options={garageTypeOptions}
                id={`${fieldName}.building.garageType`}
                name={`${fieldName}.building.garageType`}
                mode="light"
                label="Garage Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.garageType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.roofYear`}
                name={`${fieldName}.building.roofYear`}
                mode="light"
                label="Roof Year"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofYear) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                id={`${fieldName}.building.roofType`}
                name={`${fieldName}.building.roofType`}
                type="select"
                options={roofTypeOptions}
                mode="light"
                label="Roof Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.roofShape`}
                name={`${fieldName}.building.roofShape`}
                type="select"
                options={roofShapeOptions}
                mode="light"
                label="Roof Shape"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofShape) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.primaryHeatingSource`}
                name={`${fieldName}.building.primaryHeatingSource`}
                type="select"
                options={heatingOptions}
                mode="light"
                label="Primary Heating"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.primaryHeatingSource) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.secondaryHeatingSource`}
                name={`${fieldName}.building.secondaryHeatingSource`}
                type="select"
                options={heatingOptions}
                mode="light"
                label="Secondary Heating"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.secondaryHeatingSource) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="numeric"
                id={`${fieldName}.building.numSecondaryHeatingSources`}
                name={`${fieldName}.building.numSecondaryHeatingSources`}
                mode="light"
                label="Heating units"
                disabled={disableRiskForm || !isSelectedSecondaryHeatingSourceUnitCountBased}
                helperText="Acceptable range: 1-1000"
                onChange={(val) => {
                  if (val !== initialBuilding.numSecondaryHeatingSources) {
                    toastRCEWarning();
                  }
                }}
              />
              <RenovationFieldsForm
                basePath={fieldName}
                disabled={disableRiskForm}
                onFieldChange={() => toastRCEWarning()}
              />
              <Field
                xs={6}
                id={`${fieldName}.connectedHome.theftPreventionDeviceType`}
                name={`${fieldName}.connectedHome.theftPreventionDeviceType`}
                type="select"
                options={theftPreventionDevicesOptions}
                mode="light"
                label="Theft Protective Device"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialConnectedHome.theftPreventionDeviceType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
                name={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
                options={fireProtectiveDevicesOptions}
                type="select"
                mode="light"
                label="Fire Protective Device"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialConnectedHome.fireProtectiveDeviceType) {
                    toastRCEWarning();
                  }
                }}
              />

              <Field
                name={`${fieldName}.connectedHome.internetMonitoring`}
                id={`${fieldName}.connectedHome.internetMonitoring`}
                type="checkbox"
                label="Internet connected monitoring device(s)?"
                mode="light"
                xs={6}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialConnectedHome.internetMonitoring) {
                    toastRCEWarning();
                  }
                }}
              />
            </>
          )}

          {/* Barndominium specific fields */}
          {isBarndominium && (
            <BarndomininumDwellingForm
              basePath={fieldName}
              isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
              isSelectedSecondaryHeatingSourceUnitCountBased={isSelectedSecondaryHeatingSourceUnitCountBased}
              disableRiskForm={disableRiskForm}
              initialBuilding={initialBuilding}
              initialConnectedHome={initialConnectedHome}
              toastRCEWarning={toastRCEWarning}
              state={state}
              setFieldValue={setFieldValue}
            />
          )}

          {/* Mobile Home specific fields */}
          {isMobileHome && (
            <MobileHomeForm
              basePath={fieldName}
              isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
              isSelectedSecondaryHeatingSourceUnitCountBased={isSelectedSecondaryHeatingSourceUnitCountBased}
              disableRiskForm={disableRiskForm}
              initialBuilding={initialBuilding}
              initialConnectedHome={initialConnectedHome}
              toastRCEWarning={toastRCEWarning}
              state={state}
              riskType={riskType}
              setFieldValue={setFieldValue}
            />
          )}

          {(hasBeenRated || coverageHasBeenUpdatedRef.current) && (
            <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
              <Grid container xs={8} className={classes.rceValues}>
                {/* Issue #28616: Changed to show only the active valuation (ACV for FO1) instead of both RCE and ACV */}
                <Label>
                  {item.coverages?.lossSettlement === 'ACV'
                    ? 'Our estimated actual cash value based on age is'
                    : 'Our estimated cost to replace this home'}
                </Label>
                <Tooltip
                  label="More info"
                  text={
                    item.coverages?.lossSettlement === 'ACV'
                      ? 'ACV (actual cash value) is calculated using 360Value and accounts for depreciation based on age and condition.'
                      : 'RCE (Replacement Cost Estimate) is calculated using 360Value. This is the cost to replace the dwelling with new materials. The selected loss settlement option determines which value applies to the claim.'
                  }
                />
              </Grid>
              <Grid container item xs={4} spacing={2} justifyContent="flex-end">
                <Grid item className={classes.rceValues}>
                  <Label>{item.coverages?.lossSettlement === 'ACV' ? 'ACV' : 'RCE'}</Label>
                  <Label type="infoValueBold">
                    {item.coverages?.lossSettlement === 'ACV'
                      ? item.building?.actualCashValueCalculation?.total
                        ? `$${item.building.actualCashValueCalculation.total.toLocaleString('en-US')}`
                        : 'N/A'
                      : item.building?.replacementCostEstimate?.total
                        ? `$${item.building.replacementCostEstimate.total.toLocaleString('en-US')}`
                        : 'N/A'}
                  </Label>
                </Grid>
              </Grid>
            </NotificationCard>
          )}
        </Grid>

        {(hasBeenRated || coverageHasBeenUpdatedRef.current) && (
          <Grid container spacing={2} className={classes.coverageCard}>
            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle">Base coverages</Label>
            </Grid>
            <Grid item xs={12}>
              <LabelTooltip
                label="Dwelling Limit (A)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'The amount we will pay to rebuild the home is based on the replacement cost estimate value—calculated using current construction, materials and labor costs. If you want added peace of mind, adjust the amount.'
                }}
              >
                <Field
                  id={`${fieldName}.coverages.coverageA`}
                  name={`${fieldName}.coverages.coverageA`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disableRiskForm}
                  description={
                    !getIn(errors, `${fieldName}.coverages.coverageA`)
                      ? getCoverageAHelpText({ item, state, riskType, initialCoverageA })
                      : undefined
                  }
                />
              </LabelTooltip>
            </Grid>

            <div className={classes.coverageToggleContainer}>
              <Grid container justifyContent="space-between">
                <Field
                  id={`${fieldName}.coverages.canEditCoverageAmount`}
                  name={`${fieldName}.coverages.canEditCoverageAmount`}
                  type="switch"
                  mode="dark"
                  label="Adjust dwelling limit more"
                  tooltipText="You may adjust the dwelling limit to an even higher amount with justification"
                  tooltipLabel="More Info"
                  xs={8}
                  disabled={disableRiskForm}
                  onChange={(_value, isToggledOn) => {
                    // When toggling off, reset coverageA to ACV (FO1 always uses ACV)
                    // See: https://github.com/gobranch/branch/issues/28969
                    if (!isToggledOn) {
                      const acv = item.building?.actualCashValueCalculation?.total;
                      const rce = item.building?.replacementCostEstimate?.total;
                      const sqFt = item.building?.sqFt;
                      const propertyType = item.building?.propertyType;

                      if (acv) {
                        let resetValue = acv;
                        // Apply mobile home minimum floor from brandConfig
                        if (propertyType === 'MH' && sqFt) {
                          resetValue = Math.max(resetValue, sqFt * mobileHomeMinPerSqFt);
                        }
                        setFieldValue(`${fieldName}.coverages.coverageA`, resetValue);
                      } else if (rce) {
                        // Fallback to RCE if ACV is not available
                        let fallbackValue = rce;
                        // Apply mobile home minimum floor from brandConfig
                        if (propertyType === 'MH' && sqFt) {
                          fallbackValue = Math.max(fallbackValue, sqFt * mobileHomeMinPerSqFt);
                        }
                        setFieldValue(`${fieldName}.coverages.coverageA`, fallbackValue);
                      }
                    }
                  }}
                />
                <Grid className={classes.linkAndIcon}>
                  <a
                    rel="noreferrer noopener"
                    target="_blank"
                    href="https://www.360-value.com/apps/iv/ui/#/login"
                    className={classes.link}
                  >
                    ISO 360 Login
                    <img src={externalLinkIcon} alt="link to ISO 360 login" />
                  </a>
                </Grid>

                {item.coverages?.canEditCoverageAmount && (
                  <Grid item xs={12} className={classes.adjustCoverageSubContainer}>
                    <Field
                      id={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                      name={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                      type="textarea"
                      mode="dark"
                      label="Description of reason for adjusting dwelling limit (required)"
                    />
                  </Grid>
                )}
              </Grid>
            </div>

            <Grid item xs={4}>
              <LabelTooltip
                label="Other Structures (B)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'Provides coverage for buildings and structures on the insured property that are separate from the main dwelling but within 250 feet, such as detached garages, sheds, and fences. This does not include buildings or structures designed for farming use. To add additional related private structure coverage, schedule them below.'
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageB`}
                  name={`${fieldName}.coverages.coverageB`}
                  type="value"
                  mode="dark"
                  value={formatCoverageB(item.coverages)}
                  disabled={disableRiskForm}
                  fast={false} // need this so react updates the options when coverageA changes
                  riskType={riskType}
                  state={state}
                  classes={classes}
                  coverages={riskCoverages}
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={4}>
              <LabelTooltip
                label="Personal Property (C)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'If something happens to personal property, like theft or damage due to fire, this covers those items.'
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageC`}
                  name={`${fieldName}.coverages.coverageC`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disableRiskForm}
                  riskType={riskType}
                  state={state}
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={4}>
              <LabelTooltip
                xs={4}
                label="Addit. Living Cost (D)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'If the home becomes uninhabitable due to some kind of covered loss, this coverage provides temporary housing up to the amount entered here.'
                }}
              >
                <IsolatedCoverageD
                  fieldName={fieldName}
                  disabled={disableRiskForm || isCoverageDDisabled}
                  userEditingRef={userEditingCoverageDRef}
                  onFocus={handleCoverageDFocus}
                  onBlur={handleCoverageDBlur}
                />
              </LabelTooltip>
            </Grid>

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Deductibles
              </Label>
            </Grid>
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleAllOther`}
              name={`${fieldName}.coverages.deductibleAllOther`}
              type="select"
              mode="dark"
              label="All peril deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleWindHail`}
              name={`${fieldName}.coverages.deductibleWindHail`}
              type="select"
              options={windHailDeductibleOptions}
              mode="dark"
              label="Wind & Hail deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              coverages={riskCoverages}
              state={state}
              coverage={riskCoverages?.coverageA}
              description="Wind and hail deductible must match an All Peril when it's value is greater than 2% of coverage A"
            />

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Optional Coverages
              </Label>
            </Grid>

            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.lossSettlement`}
              name={`${fieldName}.coverages.lossSettlement`}
              type="select"
              options={lossSettlementOptions}
              mode="dark"
              label="Building Loss Settlement"
              disabled
              riskType={riskType}
              state={state}
            />

            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.roofLossSettlement`}
              name={`${fieldName}.coverages.roofLossSettlement`}
              type="select"
              options={roofLossSettlementOptions}
              mode="dark"
              label="Roof Loss Settlement"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              coverages={riskCoverages}
              riskFieldName={fieldName}
            />

            <BrandFormField
              name={`${fieldName}.coverages.coverageMS`}
              id={`${fieldName}.coverages.coverageMS`}
              type="select"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              coverages={riskCoverages}
              county={address?.county}
              targetCoverage={coverageA}
              targetCoverageLimit={mineSubsidenceCoverageLimit}
              helperTextLabel="Matches coverage A"
            />
            <Field
              name={`${fieldName}.coverages.hasActiveCoverageMS`}
              id={`${fieldName}.coverages.hasActiveCoverageMS`}
              type="checkbox"
              label="This dwelling has active mine subsidence or unrepaired mine damage"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
            />

            {/* checkboxes */}
            <BrandFormField
              name={`${fieldName}.coverages.repairRebuildRequirement`}
              id={`${fieldName}.coverages.repairRebuildRequirement`}
              type="checkbox"
              label="Repair or rebuilding requirement"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              riskType={riskType}
              riskFieldName={fieldName}
              state={state}
            />

            <Field
              name={`${fieldName}.coverages.serviceLine`}
              id={`${fieldName}.coverages.serviceLine`}
              type="checkbox"
              label="Service line coverage"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
            />
            {isMobileHome && (
              <BrandFormField
                name={`${fieldName}.coverages.replCostMobileHome`}
                id={`${fieldName}.coverages.replCostMobileHome`}
                type="checkbox"
                label="Replacement cost terms for mobile home"
                mode="dark"
                xs={6}
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
                coverages={riskCoverages}
              />
            )}

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Exclusions/Restrictions
              </Label>
            </Grid>

            {/* cosmetic damage exclusion */}
            <CosmeticDamageExclusionSection
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            <RoofExclusionSection
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            <ExteriorWallSurfacingExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

            <OtherStructuresExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

            {inPolicyRoute && (
              <StormDamageExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
            )}

            <SwimmingPoolForm fieldName={fieldName} disabled={disableRiskForm} />
            <EarthquakeCoverageForm
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            {/* inland marine */}
            <InlandMarineForm
              fieldName={`${fieldName}.coverages.scheduleCoverages`}
              disabled={disableRiskForm}
              state={state}
              riskType={FarmRiskType.Fo1}
              coverages={riskCoverages}
            />
            {/* related private structures */}
            {availableCoverageTypes.includes(FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES) && (
              <RelatedPrivateStructuresForm
                fieldName={`${fieldName}.coverages.scheduleCoverages`}
                disabled={disableRiskForm}
                address={locationAddress}
                riskType={riskType}
                coverages={riskCoverages}
              />
            )}

            {/* well pump */}
            <WellPumpForm fieldName={`${fieldName}`} disabled={disableRiskForm} />

            {/* dwelling under construction */}
            <DwellingUnderConstructionForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
          </Grid>
        )}
      </Suspense>
    </CollapsibleCard>
  );
};

FO1.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  canRemoveRisk: PropTypes.bool.isRequired,
  removeRisk: PropTypes.func.isRequired,
  setRiskFieldnameToEdit: PropTypes.func.isRequired,
  isLastRisk: PropTypes.bool.isRequired,
  locationAddress: PropTypes.object.isRequired
};

export default withDatePicker(FO1);
